import { useEffect, useMemo, useState } from "react";
import { OnScreenModal } from "@/components/ui/OnScreenModal";
import { formatDateTime } from "../../utils/applicationFormatters";
import {
  buildIframeSrcDoc,
  isMailLogVisibleUnderFilters,
  parseMailLogRow,
  toggleMailConversationFilter,
  type MailConversationFilterKey,
} from "../../utils/mailLogHelpers";
import type { ApplicationMailConversationModalProps } from "../../types/modals";

const tid = "application-modal-application-mail-conversation";

/**
 * Angular EmailParserDetail.showContent:
 * `iframe.height = contentDocument.documentElement.scrollHeight + 'px'`
 * (no fixed height — grows to fit the mail body).
 *
 * `documentElement.scrollHeight` in a standards-mode document is clamped to at
 * least the iframe's current viewport height (browser default ~150px), which
 * leaves dead whitespace on short emails and, when content settles after load,
 * a too-small height with an internal scrollbar. Collapse the iframe first so
 * the viewport can't clamp the measurement, then grow it to the true content
 * height — matching Angular's "fit to content" behaviour exactly.
 */
function resizeMailIframeToContent(iframe: HTMLIFrameElement) {
  try {
    const doc = iframe.contentDocument;
    if (!doc?.documentElement) return;
    iframe.style.height = "0px";
    const height = Math.max(
      doc.documentElement.scrollHeight,
      doc.body?.scrollHeight ?? 0,
    );
    iframe.style.height = height > 0 ? `${height}px` : "";
  } catch {
    // Ignore cross-origin / empty document.
  }
}

function handleMailIframeLoad(iframe: HTMLIFrameElement) {
  resizeMailIframeToContent(iframe);
  // Angular showContent uses setTimeout(10) before measuring; remeasure after layout.
  window.setTimeout(() => resizeMailIframeToContent(iframe), 10);
  // Re-measure once late content (images) finishes loading so the mail body is
  // never clipped behind an inner scrollbar.
  const doc = iframe.contentDocument;
  if (!doc) return;
  doc.querySelectorAll("img").forEach((img) => {
    if (img.complete) return;
    const remeasure = () => resizeMailIframeToContent(iframe);
    img.addEventListener("load", remeasure, { once: true });
    img.addEventListener("error", remeasure, { once: true });
  });
}

export function ApplicationMailConversationModal({
  isOpen,
  onClose,
  mailLogs,
  scrollToMailId,
  applicantEmail = "",
  onReplyToMail,
}: ApplicationMailConversationModalProps) {
  const rows = Array.isArray(mailLogs) ? mailLogs : [];
  const [filters, setFilters] = useState<MailConversationFilterKey[]>([]);

  useEffect(() => {
    if (!isOpen) setFilters([]);
  }, [isOpen]);

  // Angular Init: delay scrollIntoView until iframes have painted.
  useEffect(() => {
    if (!isOpen || !scrollToMailId) return;
    const handle = window.setTimeout(() => {
      const el = document.getElementById(`mailid_${scrollToMailId}`);
      if (el) el.scrollIntoView({ block: "start", behavior: "auto" });
    }, 200);
    return () => window.clearTimeout(handle);
  }, [isOpen, scrollToMailId, filters]);

  const visibleRows = useMemo(() => {
    return rows
      .map((log, index) => ({ parsed: parseMailLogRow(log, index), index }))
      .filter(({ parsed }) =>
        isMailLogVisibleUnderFilters(filters, parsed, applicantEmail),
      );
  }, [rows, filters, applicantEmail]);

  return (
    <OnScreenModal
      isOpen={isOpen}
      title="Mail Detail"
      onClose={onClose}
      maxWidth="max-w-900"
      bodyScrollOnly
      // Angular mat-dialog-content default: max-height 65vh + overflow auto.
      bodyClassName="max-h-[65vh] overflow-y-auto !px-5 !pt-4 !pb-5"
      overlayDataTestId={tid}
      panelDataTestId={`${tid}-panel`}
      titleDataTestId={`${tid}-title`}
      closeButtonDataTestId={`${tid}-close-btn`}
    >
      <div className="pb-1" data-testid={`${tid}-body`}>
        <div
          className="mb-4 flex flex-wrap gap-x-10 gap-y-3"
          data-testid={`${tid}-filters`}
        >
          <label
            className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700"
            data-testid={`${tid}-filter-forapplicant-label`}
          >
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-[#1597ff] focus:ring-[#1597ff]"
              checked={filters.includes("forapplicant")}
              data-testid={`${tid}-filter-forapplicant-checkbox`}
              onChange={() =>
                setFilters((prev) =>
                  toggleMailConversationFilter(prev, "forapplicant"),
                )
              }
            />
            Applicant Communication Only
          </label>
          <label
            className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700"
            data-testid={`${tid}-filter-showreply-label`}
          >
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-[#1597ff] focus:ring-[#1597ff]"
              checked={filters.includes("showreply")}
              data-testid={`${tid}-filter-showreply-checkbox`}
              onChange={() =>
                setFilters((prev) =>
                  toggleMailConversationFilter(prev, "showreply"),
                )
              }
            />
            Show Replies
          </label>
        </div>

        {rows.length === 0 ? (
          <p className="text-sm text-slate-600" data-testid={`${tid}-empty`}>
            No mail history.
          </p>
        ) : (
          <div className="space-y-0" data-testid={`${tid}-mail-list`}>
            {visibleRows.map(({ parsed, index }, visibleIndex) => {
              const isImap = parsed.source === "imap";
              const sentLabel = isImap ? "Received" : "Sent";
              const formattedDate = formatDateTime(parsed.createdAt);
              return (
                <div key={`${parsed.id}-${index}`}>
                  <div
                    id={`mailid_${parsed.id}`}
                    data-testid={`${tid}-row-${parsed.id}`}
                    className={`px-2 py-3 ${isImap ? "bg-[#e0e1e3]" : "bg-white"}`}
                  >
                    <div
                      className="flex flex-wrap justify-between gap-2 text-sm"
                      data-testid={`${tid}-row-${parsed.id}-header`}
                    >
                      <span data-testid={`${tid}-row-${parsed.id}-to`}>
                        <strong>To</strong> : {parsed.to || "—"}
                      </span>
                      <span
                        className="text-slate-700"
                        data-testid={`${tid}-row-${parsed.id}-date`}
                      >
                        <strong>{sentLabel}</strong>
                        {formattedDate ? ` : ${formattedDate}` : ""}
                      </span>
                    </div>
                    <div
                      className="mt-2 flex flex-wrap items-start justify-between gap-2 text-sm"
                      data-testid={`${tid}-row-${parsed.id}-subject-row`}
                    >
                      <span data-testid={`${tid}-row-${parsed.id}-subject`}>
                        <strong>Subject</strong> : {parsed.subject || "—"}
                      </span>
                      {isImap && onReplyToMail ? (
                        <button
                          type="button"
                          className="shrink-0 text-sm font-medium text-[#1597ff] underline"
                          data-testid={`${tid}-row-${parsed.id}-reply-btn`}
                          onClick={() =>
                            onReplyToMail({ subject: parsed.subject })
                          }
                        >
                          Reply
                        </button>
                      ) : null}
                    </div>
                    <iframe
                      title={`Mail body ${parsed.subject || parsed.id}`}
                      data-testid={`${tid}-row-${parsed.id}-body-iframe`}
                      className="mt-3 w-full border-0 bg-white"
                      srcDoc={buildIframeSrcDoc(parsed.htmlBody)}
                      onLoad={(e) => handleMailIframeLoad(e.currentTarget)}
                    />
                  </div>
                  {visibleIndex < visibleRows.length - 1 ? (
                    <hr
                      className="my-2 border-slate-200"
                      data-testid={`${tid}-row-${parsed.id}-divider`}
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </OnScreenModal>
  );
}
